import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import type { CFEntity, CFCommittee, CFCommitteeSummary } from '../../types/campaignFinance';
import { useCFCommittee } from '../../hooks/useCFCommittee';
import { useCFMoneyFlow } from '../../hooks/useCFMoneyFlow';
import { useCFEntity, type CFEntityScope } from '../../hooks/useCFEntity';
import CFCommitteeHeader from './CFCommitteeHeader';
import CFKpiStrip from './CFKpiStrip';
import CFFinancialSummary from './CFFinancialSummary';
import CFMoneyFlow, { type CFEntitySearchHandler } from './CFMoneyFlow';
import CFRaisedVsSpent from './CFRaisedVsSpent';
import CFTransactionsExplorer from './CFTransactionsExplorer';

interface CFCommitteeDashboardProps {
  /** Committee CO_ID (resolved from contribution rows); null falls back to name-scoped data. */
  coId: string | null;
  committeeName: string;
  startDate?: string;
  endDate?: string;
  party?: string | null;
  /** Short cycle label for the contributions caveat (e.g. "2026"). */
  cycleLabel?: string;
  /** Forwarded to the panel and explorer: entity names launch a new search. */
  onEntitySearch?: CFEntitySearchHandler;
}

/**
 * Comprehensive committee dashboard: header · KPI strip · financial-summary
 * waterfall · follow-the-money · raised-vs-spent trend · tabbed transactions
 * explorer. Fetches the committee profile and all three transaction streams,
 * scoped to the committee and (optionally) a cycle window.
 */
const CFCommitteeDashboard: React.FC<CFCommitteeDashboardProps> = ({ coId, committeeName, startDate, endDate, party, cycleLabel, onEntitySearch }) => {
  const committee = useCFCommittee();
  const moneyFlow = useCFMoneyFlow();
  const contributions = useCFEntity('contributions');
  const expenditures = useCFEntity('expenditures');
  const loans = useCFEntity('loans');
  const [activeTab, setActiveTab] = useState<CFEntity>('contributions');

  const { load: loadCommittee } = committee;
  const { load: loadFlow, reset: resetFlow } = moneyFlow;
  const { load: loadContrib } = contributions;
  const { load: loadExp } = expenditures;
  const { load: loadLoans } = loans;

  // The page resolves co_id from a contribution row, which is empty when the
  // cycle-scoped ledger has no rows in range (e.g. a committee with no current-cycle
  // activity). Fall back to resolving co_id from the committee name via the spine
  // (window-independent) so the profile/header still load.
  const [resolvedCoId, setResolvedCoId] = useState<string | null>(null);
  useEffect(() => {
    if (coId || !committeeName) { setResolvedCoId(null); return; }
    let cancelled = false;
    api.get(`/campaign-finance/committees/resolve?name=${encodeURIComponent(committeeName)}`)
      .then(res => { if (!cancelled) setResolvedCoId(res.data?.data?.co_id || null); })
      .catch(() => { if (!cancelled) setResolvedCoId(null); });
    return () => { cancelled = true; };
  }, [coId, committeeName]);

  const effectiveCoId = coId || resolvedCoId;

  useEffect(() => {
    const scope: CFEntityScope = {
      committee_co_id: effectiveCoId || undefined,
      committee_name: effectiveCoId ? undefined : committeeName,
      start_date: startDate,
      end_date: endDate,
    };
    if (effectiveCoId) {
      loadCommittee(effectiveCoId, { start_date: startDate, end_date: endDate });
      // Money-flow payload drives the follow-the-money panel (badges, previews,
      // disclosures). Without a co_id the panel falls back to client aggregates.
      loadFlow(effectiveCoId, { start_date: startDate, end_date: endDate });
    } else {
      resetFlow();
    }
    loadContrib(scope, 'none');
    loadExp(scope, 'none');
    loadLoans(scope, 'none');
    setActiveTab('contributions');
  }, [effectiveCoId, committeeName, startDate, endDate, loadCommittee, loadFlow, resetFlow, loadContrib, loadExp, loadLoans]);

  // Prefer the server profile summary; otherwise synthesize from entity aggregates
  // so the KPI strip / waterfall still render when a co_id can't be resolved.
  const effectiveSummary: CFCommitteeSummary = useMemo(() => {
    if (committee.profile?.summary) return committee.profile.summary;
    const raised = Number(contributions.summary?.total_amount) || 0;
    const spent = Number(expenditures.summary?.total_amount) || 0;
    const originated = loans.selfFunding?.originatedPrincipal || 0;
    const repaid = loans.selfFunding?.repaidPrincipal || 0;
    const outstanding = loans.selfFunding?.outstandingBalance || 0;
    return {
      total_raised: raised,
      total_spent: spent,
      cash_on_hand: raised - spent,
      outstanding_loan_balance: outstanding,
      burn_rate: raised > 0 ? spent / raised : null,
      loans_originated: originated,
      loans_repaid: repaid,
      beginning_balance: 0,
      ending_balance: 0 + raised + originated - spent - repaid,
    };
  }, [committee.profile, contributions.summary, expenditures.summary, loans.selfFunding]);

  const committeeMeta: CFCommittee | null = committee.profile?.committee
    ?? (committeeName
      ? {
          co_id: effectiveCoId || '—',
          current_name: committeeName,
          current_type: null, source: null, level: null,
          first_seen: null, last_seen: null,
          candidate_name: null, candidate_id: null,
          candidate_link_confidence: null, candidate_link_source: null,
          name_history: [],
        }
      : null);

  const entities = { contributions, expenditures, loans };

  return (
    <div className="space-y-[18px]">
      {committeeMeta && <CFCommitteeHeader committee={committeeMeta} party={party} />}

      <CFKpiStrip summary={effectiveSummary} />

      <CFFinancialSummary summary={effectiveSummary} />

      <CFMoneyFlow
        committeeName={committeeMeta?.current_name || committeeName}
        topContributors={contributions.topList}
        topPayees={expenditures.topList}
        totalIn={effectiveSummary.total_raised}
        totalOut={effectiveSummary.total_spent}
        selfFunding={loans.selfFunding}
        flow={moneyFlow.flow}
        onEntitySearch={onEntitySearch}
      />

      <CFRaisedVsSpent raised={contributions.monthlyTrend} spent={expenditures.monthlyTrend} />

      <CFTransactionsExplorer
        activeTab={activeTab}
        onTabChange={setActiveTab}
        entities={entities}
        cycleLabel={cycleLabel}
        onEntitySearch={onEntitySearch}
        committeeScoped
      />
    </div>
  );
};

export default CFCommitteeDashboard;
